import { useState, useEffect, useRef } from 'react';
import { useWindowWidth } from '../hooks/useWindowWidth.js';
import ModalDirtyContext from '../context/modalDirtyContext.js';
import DirtyGuardModal from './DirtyGuardModal.jsx';

/**
 * Modal — wraps content in an overlay panel.
 *
 * Dirty-guard (automatic — no props required):
 *   If a CrudForm child sets isDirty via ModalDirtyContext, all close paths
 *   (X button, Escape key, backdrop click) show a "Leave without saving?" dialog
 *   instead of closing immediately.
 */
export default function Modal({ title, onClose, children, preventOutsideClose = false, panelStyle, hideHeader = false }) {
  const { isMobile, isTablet } = useWindowWidth();

  /* ── Dirty guard state (populated by CrudForm via ModalDirtyContext) ───── */
  const [isDirty, setIsDirty] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);

  const handleClose = () => {
    if (isDirty) {
      setConfirmingClose(true);
    } else {
      onClose();
    }
  };

  /* Stable ref so the Escape key listener always uses the latest handleClose */
  const handleCloseRef = useRef(handleClose);
  handleCloseRef.current = handleClose;

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') handleCloseRef.current(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  /* Reset confirming state if the dirty guard clears while dialog is open */
  useEffect(() => {
    if (!isDirty) setConfirmingClose(false);
  }, [isDirty]);

  const dirtyContextValue = { setIsDirty };

  /* ── Mobile: full-screen slide-up sheet ──────────────────────────────────── */
  if (isMobile) {
    return (
      <ModalDirtyContext.Provider value={dirtyContextValue}>
        <div className="ns-modal-overlay" onClick={preventOutsideClose ? undefined : handleClose} role="button" tabIndex={0} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && !preventOutsideClose && handleClose()}>
          <div className="ns-modal-mobile-sheet" onClick={(e) => e.stopPropagation()} role="button" tabIndex={0} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && e.stopPropagation()}>
            {!hideHeader && (
              <div className="ns-modal-mobile-header">
                <button onClick={handleClose} className="ns-modal-back-btn" aria-label="Close">
                  ← Back
                </button>
                <h2 className="ns-modal-mobile-title">{title}</h2>
                {/* spacer keeps title centered */}
                <div style={{ width: '64px' }} />
              </div>
            )}
            <div className="ns-modal-mobile-body">{children}</div>
          </div>
        </div>
        {confirmingClose && (
          <DirtyGuardModal
            open={true}
            onConfirm={() => { setConfirmingClose(false); onClose(); }}
            onCancel={() => setConfirmingClose(false)}
          />
        )}
      </ModalDirtyContext.Provider>
    );
  }

  /* ── Tablet: wider modal (85vw) ─────────────────────────────────────────── */
  const panelWidthStyle = isTablet ? { maxWidth: '85vw' } : undefined;

  return (
    <ModalDirtyContext.Provider value={dirtyContextValue}>
      <div className="ns-modal-overlay" onClick={preventOutsideClose ? undefined : handleClose} role="button" tabIndex={0} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && !preventOutsideClose && handleClose()}>
        <div className="ns-modal-panel" style={{ ...panelWidthStyle, ...panelStyle }} onClick={(e) => e.stopPropagation()} role="button" tabIndex={0} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && e.stopPropagation()}>
          {!hideHeader && (
            <div className="ns-modal-header">
              <h2 className="ns-modal-title">{title}</h2>
              <button onClick={handleClose} className="ns-modal-close-btn" aria-label="Close">&times;</button>
            </div>
          )}
          <div className="ns-modal-body">{children}</div>
        </div>
      </div>
      {confirmingClose && (
        <DirtyGuardModal
          open={true}
          onConfirm={() => { setConfirmingClose(false); onClose(); }}
          onCancel={() => setConfirmingClose(false)}
        />
      )}
    </ModalDirtyContext.Provider>
  );
}
